import React, { useState } from "react";

// material-ui
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import dashboardStyle from "assets/jss/material-dashboard-react/views/dashboardStyle.js";
import EmptyState from "components/EmptyState/EmptyState.jsx";
import MapIcon from "@material-ui/icons/Map";

// Mapa de colores por estado de lote
const statusColors = {
  DISPONIBLE: "#4caf50",
  APARTADO: "#ff9800",
  CREDITOACTIVO: "#2196f3",
  LIQUIDADO: "#9e9e9e",
  PENDIENTE: "#f44336"
};

const statusLabels = {
  DISPONIBLE: "Disponible",
  APARTADO: "Apartado",
  CREDITOACTIVO: "Crédito activo",
  LIQUIDADO: "Liquidado",
  PENDIENTE: "Pendiente"
};

const FALLBACK_COLOR = "#bdbdbd";

// Datos de ejemplo de lotes (simulando datos que vendrían de la API)
const lotData = {
  L1: { lote: "L1", manzana: "M1", status: "CREDITOACTIVO", cliente: "Javier Villareal" },
  L2: { lote: "L2", manzana: "M1", status: "DISPONIBLE", cliente: "" },
  L3: { lote: "L3", manzana: "M1", status: "CREDITOACTIVO", cliente: "Daniel Cardenas" },
  L4: { lote: "L4", manzana: "M1", status: "DISPONIBLE", cliente: "" },
  L5: { lote: "L5", manzana: "M1", status: "APARTADO", cliente: "" },
  L6: { lote: "L6", manzana: "M1", status: "CREDITOACTIVO", cliente: "Lizbeth Contreras" },
  L7: { lote: "L7", manzana: "M1", status: "DISPONIBLE", cliente: "" },
  L8: { lote: "L8", manzana: "M1", status: "DISPONIBLE", cliente: "" },
  L9: { lote: "L9", manzana: "M1", status: "DISPONIBLE", cliente: "" },
  L10: { lote: "L10", manzana: "M1", status: "DISPONIBLE", cliente: "" },
  L11: { lote: "L11", manzana: "M1", status: "DISPONIBLE", cliente: "" },
  L12: { lote: "L12", manzana: "M1", status: "DISPONIBLE", cliente: "" },
  L13: { lote: "L13", manzana: "M1", status: "DISPONIBLE", cliente: "" },
  L14: { lote: "L14", manzana: "M1", status: "DISPONIBLE", cliente: "" },
  L15: { lote: "L15", manzana: "M1", status: "DISPONIBLE", cliente: "" },
  L16: { lote: "L16", manzana: "M1", status: "DISPONIBLE", cliente: "" },
  L17: { lote: "L17", manzana: "M1", status: "DISPONIBLE", cliente: "" },
  L18: { lote: "L18", manzana: "M1", status: "DISPONIBLE", cliente: "" },
  L19: { lote: "L19", manzana: "M1", status: "DISPONIBLE", cliente: "" },
  L20: { lote: "L20", manzana: "M1", status: "DISPONIBLE", cliente: "" },
  L21: { lote: "L21", manzana: "M1", status: "DISPONIBLE", cliente: "" },
  L22: { lote: "L22", manzana: "M1", status: "DISPONIBLE", cliente: "" },
  L23: { lote: "L23", manzana: "M1", status: "DISPONIBLE", cliente: "" },
  L24: { lote: "L24", manzana: "M1", status: "PENDIENTE", cliente: "" },
  L25: { lote: "L25", manzana: "M1", status: "DISPONIBLE", cliente: "" },
  L26: { lote: "L26", manzana: "M1", status: "DISPONIBLE", cliente: "" },
  L27: { lote: "L27", manzana: "M1", status: "DISPONIBLE", cliente: "" },
  L28: { lote: "L28", manzana: "M1", status: "DISPONIBLE", cliente: "" },
  L29: { lote: "L29", manzana: "M1", status: "LIQUIDADO", cliente: "" }
};

function getLotColor(lotId) {
  const lot = lotData[lotId];
  if (!lot) return FALLBACK_COLOR;
  return statusColors[lot.status] || FALLBACK_COLOR;
}

function getLotTooltip(lotId) {
  const lot = lotData[lotId];
  if (!lot) return lotId;
  const label = statusLabels[lot.status] || lot.status || "Desconocido";
  let text = `Lote: ${lot.lote}\nManzana: ${lot.manzana}\nEstado: ${label}`;
  if (lot.cliente) {
    text += `\nCliente: ${lot.cliente}`;
  }
  return text;
}

// Componente de leyenda del mapa
function MapLegend() {
  return (
    <Box display="flex" flexWrap="wrap" justifyContent="center" mt={2} mb={1}>
      {Object.keys(statusColors).map(key => (
        <Box key={key} display="flex" alignItems="center" mr={3} mb={1}>
          <Box
            style={{
              width: 18,
              height: 18,
              backgroundColor: statusColors[key],
              borderRadius: 3,
              marginRight: 6,
              border: "1px solid rgba(0,0,0,0.15)"
            }}
          />
          <Typography variant="body2" style={{ fontSize: 13 }}>
            {statusLabels[key]}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

const useStyles = makeStyles(theme => ({
  ...dashboardStyle,
  cardTitle: {
    marginTop: "0",
    minHeight: "auto",
    fontWeight: "300",
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    marginBottom: "3px",
    textDecoration: "none"
  },
  root: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    color: "#999 !important",
    fontWeight: "400 !important",
    fontSize: "14px !important",
    lineHeight: "1.42857 !important",
    letterSpacing: "unset !important"
  },
  rootB: {
    "&:before": {
      borderBottomColor: "lightgrey !important"
    },
    "&:after": {
      borderBottomColor: "lightgrey !important"
    }
  },
  container: {
    textAlign: "center"
  },
  content: {
    display: "inline-flex",
    padding: "10px",
    alignItems: "center"
  },
  paddingBottomCard: {
    paddingBottom: "40px"
  },
  marginRight: {
    marginRight: "10px"
  },
  marginBottom: {
    marginBottom: "50px"
  },
  formControl: {
    marginTop: theme.spacing(3.5),
    minWidth: "100%"
  },
  checkControl: {
    marginTop: theme.spacing(5)
  },
  formControlDate: {
    marginTop: theme.spacing(1.5),
    minWidth: "100%"
  },
  selectEmpty: {
    marginTop: theme.spacing(2)
  },
  showSpinner: {
    float: "right",
    display: "block"
  },
  hideSpinner: {
    display: "none"
  },
  disabledSelect: {
    opacity: "0.5 !important"
  },
  enableSelect: {
    opacity: "1 !important"
  },
  mapadiv: {
    width: "60%",
    margin: "auto",
    backgroundColor: "#f5f5f5",
    padding: "16px",
    borderRadius: "8px",
    "& path, & line, & polyline, & polygon": {
      stroke: "#1d1d1b",
      strokeWidth: "2px"
    }
  },
  cls1: {
    fill: "none",
    stroke: "#1d1d1b",
    strokeMiterlimit: "10"
  },
  lotGroup: {
    cursor: "pointer",
    transition: "opacity 0.2s",
    "&:hover": {
      opacity: 0.8
    }
  }
}));

// Componente para un lote individual con tooltip
function LotGroup({ id, classes, children }) {
  const color = getLotColor(id);
  const tooltipText = getLotTooltip(id);

  return (
    <Tooltip
      title={
        <span style={{ whiteSpace: "pre-line", fontSize: 13 }}>
          {tooltipText}
        </span>
      }
      arrow
      placement="top"
    >
      <g
        id={id}
        className={classes.lotGroup}
        style={{ fill: color }}
      >
        {children}
      </g>
    </Tooltip>
  );
}

export default function Desarrollo() {
  const classes = useStyles();
  // Simular si hay datos de croquis disponibles
  const [hasData] = useState(true);

  if (!hasData) {
    return (
      <EmptyState
        message="Selecciona un desarrollo para ver el croquis"
        icon={MapIcon}
      />
    );
  }

  return (
    <>
      <div className={classes.mapadiv}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 3137.86 3063.48">
          <defs></defs>
          <LotGroup id="L1" classes={classes}>
            <path
              className={classes.cls1}
              d="M2578.54,3012.91q10.29-72.59,20.57-145.18a68.73,68.73,0,0,0-12.36-28,70.2,70.2,0,0,0-35.11-24.6l-73.85-4.62q-3.27,51-6.33,102.07-2.58,43-5,86c16.84.69,35,2.09,54.25,4.5C2541.56,3005.65,2560.89,3009.08,2578.54,3012.91Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M2466.66,2995.88s49.77,3.19,73.9,7.54,38.51,5.89,38.51,5.89"
              transform="translate(-700.15 -108.92)"
            />
          </LotGroup>
          <LotGroup id="L2" classes={classes}>
            <path
              className={classes.cls1}
              d="M2466.45,2998.54,2341.57,2990l12-187.12,124.22,7.64Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M2341.72,2987.59l124.94,8.29"
              transform="translate(-700.15 -108.92)"
            />
          </LotGroup>
          <LotGroup id="L3" classes={classes}>
            <path
              className={classes.cls1}
              d="M2341.57,2990l-124.88-7.92q6.27-93.75,12.53-187.51l124.35,8.31Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M2217,2979.68l124.76,7.91"
              transform="translate(-700.15 -108.92)"
            />
          </LotGroup>
          <LotGroup id="L4" classes={classes}>
            <path
              className={classes.cls1}
              d="M2216.69,2982.05l-237.29-8.5,191.67-283.06,64.88,4Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M2174.1,2691.15,1983.55,2971"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M1983.55,2971l233.41,8.7"
              transform="translate(-700.15 -108.92)"
            />
          </LotGroup>
          <LotGroup id="L5" classes={classes}>
            <path
              className={classes.cls1}
              d="M2171.07,2690.49c13.24.79,36.24,2.16,64.88,4,74.52,4.69,113.35,7.64,212.3,14.24,15.67,1,40,2.66,70,4.61a219.4,219.4,0,0,1-6.85-80.86c-81-31.79-122.62-48.26-145.68-57.46-5.35-2.14-22-8.79-44.5-17.57q-23.52-9.18-46.78-18Z"
              transform="translate(-700.15 -108.92)"
            />
            <polyline
              className={classes.cls1}
              points="1474.49 2581.79 1539.49 2484.91 1576.26 2431.21"
            />
          </LotGroup>
          <LotGroup id="L6" classes={classes}>
            <path
              className={classes.cls1}
              d="M2274.64,2539l-27.09-28.06,136.61-129.49,119.48,125.53c6.21,8.94,15.63,25,18.72,45.63a91.82,91.82,0,0,1,.79,22.68c-.82,9.07-2.71,14.46-5,23.28a283.76,283.76,0,0,0-6.59,33.69Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M2249.17,2509.35l25.81,27a4.1,4.1,0,0,1,1.46,2.45,3.73,3.73,0,0,1,0,1.36"
              transform="translate(-700.15 -108.92)"
            />
          </LotGroup>
          <LotGroup id="L7" classes={classes}>
            <path
              className={classes.cls1}
              d="M2384.16,2381.4l-136.61,129.49-85.71-90.73,136.08-129,.28.29Z"
              transform="translate(-700.15 -108.92)"
            />
            <line
              className={classes.cls1}
              x1="1549.02"
              y1="2400.43"
              x2="1463.01"
              y2="2309.99"
            />
          </LotGroup>
          <LotGroup id="L8" classes={classes}>
            <path
              className={classes.cls1}
              d="M2161.84,2420.16l136.08-129-87-90.46-135,130Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M2077.44,2329.24l85.72,89.67"
              transform="translate(-700.15 -108.92)"
            />
          </LotGroup>
          <LotGroup id="L9" classes={classes}>
            <path
              className={classes.cls1}
              d="M2075.86,2330.64l135-129.9-86.11-90.3-135.4,130.35Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M1991.05,2239.19q43.2,45,86.39,90"
              transform="translate(-700.15 -108.92)"
            />
          </LotGroup>
          <LotGroup id="L10" classes={classes}>
            <path
              className={classes.cls1}
              d="M1989.38,2240.79q-43.39-45.48-86.8-90.95l135.56-129.24,86.64,89.84Z"
              transform="translate(-700.15 -108.92)"
            />
            <line
              className={classes.cls1}
              x1="1204.06"
              y1="2039.36"
              x2="1290.29"
              y2="2129.82"
            />
          </LotGroup>
          <LotGroup id="L11" classes={classes}>
            <path
              className={classes.cls1}
              d="M2038.14,2020.6q-43.22-45.33-86.44-90.65l-135.1,129.56,86,90.33Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M1817.92,2058.25l86,89.73"
              transform="translate(-700.15 -108.92)"
            />
          </LotGroup>
          <LotGroup id="L12" classes={classes}>
            <path
              className={classes.cls1}
              d="M1865.46,1839.35l86.24,90.6-135.1,129.56q-43.12-45.2-86.26-90.38Z"
              transform="translate(-700.15 -108.92)"
            />
            <line
              className={classes.cls1}
              x1="1031.73"
              y1="1858.73"
              x2="1117.77"
              y2="1949.32"
            />
          </LotGroup>
          <LotGroup id="L13" classes={classes}>
            <path
              className={classes.cls1}
              d="M1645.49,1876.91"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M1645.49,1876.91"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M1730.34,1969.13l-86.61-90.22L1779,1749l86.44,90.39Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M1731.88,1967.65q-43.34-45.08-86.66-90.17"
              transform="translate(-700.15 -108.92)"
            />
          </LotGroup>
          <LotGroup id="L14" classes={classes}>
            <path
              className={classes.cls1}
              d="M1643.73,1878.91l-86.25-90.39L1692.78,1659l86.24,90Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M1559.07,1787.08l86.07,90.47"
              transform="translate(-700.15 -108.92)"
            />
          </LotGroup>
          <LotGroup id="L15" classes={classes}>
            <path
              className={classes.cls1}
              d="M1692.78,1659l-86.83-90.6-136.09,129.07,87.62,91.09Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M1471.37,1695.87l87.63,91.2"
              transform="translate(-700.15 -108.92)"
            />
          </LotGroup>
          <LotGroup id="L16" classes={classes}>
            <path
              className={classes.cls1}
              d="M1469.86,1697.43,1384,1605.09l137.46-128.33,84.54,91.6Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M1385.51,1603.62l85.86,92.25"
              transform="translate(-700.15 -108.92)"
            />
          </LotGroup>
          <LotGroup id="L17" classes={classes}>
            <path
              className={classes.cls1}
              d="M1384,1605.09l-85.06-91.25,136.88-128.58,85.64,91.5Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M1300.45,1512.37l85.06,91.25"
              transform="translate(-700.15 -108.92)"
            />
          </LotGroup>
          <LotGroup id="L18" classes={classes}>
            <path
              className={classes.cls1}
              d="M1298.89,1513.84l-85.32-92,137-127.91,85.19,91.38Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M1214.87,1420.58q42.8,45.9,85.58,91.79"
              transform="translate(-700.15 -108.92)"
            />
          </LotGroup>
          <LotGroup id="L19" classes={classes}>
            <path
              className={classes.cls1}
              d="M1213.57,1421.79l-85-91.25,137.14-128.18,84.92,91.52Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M1130,1329.14l84.86,91.44"
              transform="translate(-700.15 -108.92)"
            />
          </LotGroup>
          <LotGroup id="L20" classes={classes}>
            <path
              className={classes.cls1}
              d="M1128.52,1330.54q-42.6-46-85.19-91.91l137-127.78,85.32,91.51Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M1045.11,1237.24l84.9,91.9"
              transform="translate(-700.15 -108.92)"
            />
          </LotGroup>
          <LotGroup id="L21" classes={classes}>
            <path
              className={classes.cls1}
              d="M1180.34,1110.85l-84.81-91.5-137.91,128,85.71,91.26Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M959.22,1145.87l85.89,91.37"
              transform="translate(-700.15 -108.92)"
            />
          </LotGroup>
          <LotGroup id="L22" classes={classes}>
            <path
              className={classes.cls1}
              d="M957.62,1147.37l-84.93-91.12L1010,927.68l85.56,91.67Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M874.4,1054.66q42.4,45.6,84.82,91.21"
              transform="translate(-700.15 -108.92)"
            />
          </LotGroup>
          <LotGroup id="L23" classes={classes}>
            <path
              className={classes.cls1}
              d="M1010,927.68l-66.86-71.6-241.69,36.7,61.69,59s23.34,19.26,25.71,21.23,28.49,22.82,31,25.32,52.88,57.89,52.88,57.89Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M703.68,892.37l50.76,48.68s14.9,13.18,19.12,16.88,17.14,14.5,17.14,14.5c2.77,2.05,6.62,5,11.14,8.49h0c7.89,6.57,17.25,14.14,20,16.44,4.79,4.06,3.64,3.78,29.37,32.06,2.58,2.85,4.8,5.29,7.3,8,6.52,7.13,12,13,15.92,17.22"
              transform="translate(-700.15 -108.92)"
            />
          </LotGroup>
          <LotGroup id="L24" classes={classes}>
            <path
              className={classes.cls1}
              d="M943.16,856l51.58-85.46L835,672.22l-134.21,220,.62.51Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M703.21,892.51q13.59-22.32,27.16-44.64,53.21-87.29,106.39-174.57"
              transform="translate(-700.15 -108.92)"
            />
          </LotGroup>
          <LotGroup id="L25" classes={classes}>
            <path
              className={classes.cls1}
              d="M994.74,770.53,835,672.22,900.12,566,1060,663.81Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M836.76,673.3,901.9,567.05"
              transform="translate(-700.15 -108.92)"
            />
          </LotGroup>
          <LotGroup id="L26" classes={classes}>
            <path
              className={classes.cls1}
              d="M1060,663.81,900.12,566l64.75-106.49,160.61,97.72Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M966.81,460.67q-32.46,53.19-64.91,106.38"
              transform="translate(-700.15 -108.92)"
            />
          </LotGroup>
          <LotGroup id="L27" classes={classes}>
            <path
              className={classes.cls1}
              d="M1125.48,557.2,964.87,459.48l65.27-107,160,98.11Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M966.81,460.67l65.53-106.85"
              transform="translate(-700.15 -108.92)"
            />
          </LotGroup>
          <LotGroup id="L28" classes={classes}>
            <path
              className={classes.cls1}
              d="M1190.16,450.58,1255.77,344l-160.09-98.37-65.54,106.81Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M1032.34,353.82,1097.79,247"
              transform="translate(-700.15 -108.92)"
            />
          </LotGroup>
          <LotGroup id="L29" classes={classes}>
            <path
              className={classes.cls1}
              d="M1395.09,308.56l-54.13,88L1095.68,245.66q41.74-68,83.47-136Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M1097.79,247q41.09-67.12,82.16-134.24"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M1180,112.74l213.9,197.82"
              transform="translate(-700.15 -108.92)"
            />
          </LotGroup>
          {/* Segunda fila de lotes (Nuevo_L33 a L1-2) */}
          <g id="Nuevo_L33" data-name="Nuevo L33" style={{ fill: FALLBACK_COLOR }}>
            <path
              className={classes.cls1}
              d="M1382.43,563.73l-91.85-85.19,104.51-170,120.66,110.77Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M1393.85,310.56,1514,421.19"
              transform="translate(-700.15 -108.92)"
            />
          </g>
          <g id="Nuevo_L32" data-name="Nuevo L32" style={{ fill: FALLBACK_COLOR }}>
            <polygon
              className={classes.cls1}
              points="815.6 310.41 907.77 395.06 774.46 539.46 682.28 454.8 815.6 310.41"
            />
          </g>
          <g id="Nuevo_L31" data-name="Nuevo L31" style={{ fill: FALLBACK_COLOR }}>
            <path
              className={classes.cls1}
              d="M1565.79,733.24l-91.19-84.86L1607.92,504l91.59,84.66Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M1606.21,505.85l91.57,84.66"
              transform="translate(-700.15 -108.92)"
            />
          </g>
          <g id="Nuevo_L30" data-name="Nuevo L30" style={{ fill: FALLBACK_COLOR }}>
            <path
              className={classes.cls1}
              d="M1657.77,817.9l-92-84.66,133.72-144.59,91.78,85.45Z"
              transform="translate(-700.15 -108.92)"
            />
            <line
              className={classes.cls1}
              x1="997.91"
              y1="481.56"
              x2="1089.21"
              y2="567.25"
            />
          </g>
          <g id="Nuevo_L29" data-name="Nuevo L29" style={{ fill: FALLBACK_COLOR }}>
            <path
              className={classes.cls1}
              d="M1749.55,903.35l-91.78-85.45,133.52-143.8,91.38,85Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M1789.36,676.17l91.4,85.05"
              transform="translate(-700.15 -108.92)"
            />
          </g>
          <g id="Nuevo_L28" data-name="Nuevo L28" style={{ fill: FALLBACK_COLOR }}>
            <path
              className={classes.cls1}
              d="M1841.13,988.21l-91.58-84.86,133.12-144.2,91.52,84.93Q1907.67,916.14,1841.13,988.21Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M1880.76,761.22l91.53,84.92"
              transform="translate(-700.15 -108.92)"
            />
          </g>
          <g id="Nuevo_L27" data-name="Nuevo L27" style={{ fill: FALLBACK_COLOR }}>
            <path
              className={classes.cls1}
              d="M1932.91,1073.26l-91.78-85q66.52-72.08,133.06-144.13l91.65,85.18Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M1972.29,846.14l91.51,85.33"
              transform="translate(-700.15 -108.92)"
            />
          </g>
          <g id="Nuevo_L26" data-name="Nuevo L26" style={{ fill: FALLBACK_COLOR }}>
            <path
              className={classes.cls1}
              d="M2024.69,1157.92l-91.78-84.66,132.93-144,91.78,84.79Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M2063.8,931.47l91.77,84.8"
              transform="translate(-700.15 -108.92)"
            />
          </g>
          <g id="Nuevo_L25" data-name="Nuevo L25" style={{ fill: FALLBACK_COLOR }}>
            <path
              className={classes.cls1}
              d="M2116.21,1242.71l-91.52-84.79,132.93-143.87L2249.4,1099Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M2155.57,1016.27l91.78,84.91"
              transform="translate(-700.15 -108.92)"
            />
          </g>
          <g id="Nuevo_L24" data-name="Nuevo L24" style={{ fill: FALLBACK_COLOR }}>
            <path
              className={classes.cls1}
              d="M2208,1327.51l-91.78-84.8L2249.4,1099,2341,1183.9Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M2247.35,1101.18l91.77,84.79"
              transform="translate(-700.15 -108.92)"
            />
          </g>
          <g id="Nuevo_L23" data-name="Nuevo L23" style={{ fill: FALLBACK_COLOR }}>
            <path
              className={classes.cls1}
              d="M2299.64,1412.56l-91.65-85L2341,1183.9l91.52,85.32Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M2339.12,1186l91.72,85.11"
              transform="translate(-700.15 -108.92)"
            />
          </g>
          <g id="Nuevo_L22" data-name="Nuevo L22" style={{ fill: FALLBACK_COLOR }}>
            <path
              className={classes.cls1}
              d="M2391.29,1497.62l-91.65-85.06,132.92-143.34,91.39,85.19Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M2430.84,1271.08l91.64,84.91"
              transform="translate(-700.15 -108.92)"
            />
          </g>
          <g id="Nuevo_L21" data-name="Nuevo L21" style={{ fill: FALLBACK_COLOR }}>
            <path
              className={classes.cls1}
              d="M2615.2,1439.86,2524,1354.41l-132.66,143.21,91.91,84.92Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M2522.48,1356l91.05,85.67"
              transform="translate(-700.15 -108.92)"
            />
          </g>
          <g id="Nuevo_L20" data-name="Nuevo L20" style={{ fill: FALLBACK_COLOR }}>
            <path
              className={classes.cls1}
              d="M2574.71,1667.33l-91.51-84.79,132-142.68,91,85.32Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M2613.53,1441.66l91,85.3"
              transform="translate(-700.15 -108.92)"
            />
          </g>
          <g id="Nuevo_L19" data-name="Nuevo L19" style={{ fill: FALLBACK_COLOR }}>
            <path
              className={classes.cls1}
              d="M2666.49,1752.25l-91.78-84.92,131.48-142.15,91.12,85.84Q2731.89,1681.64,2666.49,1752.25Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M2704.53,1527l91.25,85.71"
              transform="translate(-700.15 -108.92)"
            />
          </g>
          <g id="Nuevo_L18" data-name="Nuevo L18" style={{ fill: FALLBACK_COLOR }}>
            <path
              className={classes.cls1}
              d="M2758.93,1837.57l-92.44-85.32q65.41-70.61,130.82-141.23l90.72,86Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M2795.78,1612.67l90.73,86"
              transform="translate(-700.15 -108.92)"
            />
          </g>
          <g id="L17_2" style={{ fill: FALLBACK_COLOR }}>
            <path
              className={classes.cls1}
              d="M2758.93,1837.57l91.26,84.27q64.39-69.46,128.8-138.91L2888,1697Z"
              transform="translate(-700.15 -108.92)"
            />
            <line
              className={classes.cls1}
              x1="2186.36"
              y1="1589.73"
              x2="2277.42"
              y2="1675.77"
            />
          </g>
          <g id="Nuevo_L16" data-name="Nuevo L16" style={{ fill: FALLBACK_COLOR }}>
            <path
              className={classes.cls1}
              d="M2942,2007l-91.78-85.18q64.39-69.46,128.8-138.91l91,85.63Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M2977.3,1784.44l90.86,85.44"
              transform="translate(-700.15 -108.92)"
            />
          </g>
          <g id="Nuevo_L15" data-name="Nuevo L15" style={{ fill: FALLBACK_COLOR }}>
            <path
              className={classes.cls1}
              d="M3034.27,2092.08,2942,2007l128-138.46,91.65,85.71Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M3068.4,1870.31l91.61,85.75"
              transform="translate(-700.15 -108.92)"
            />
          </g>
          <g id="Nuevo_L14" data-name="Nuevo L14" style={{ fill: FALLBACK_COLOR }}>
            <path
              className={classes.cls1}
              d="M3125.4,2177.13l-91.13-85.05,127.39-137.81,91.25,84.8Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M3160,1956.06l91.18,84.87"
              transform="translate(-700.15 -108.92)"
            />
          </g>
          <g id="Nuevo_L13" data-name="Nuevo L13" style={{ fill: FALLBACK_COLOR }}>
            <path
              className={classes.cls1}
              d="M3216.78,2261.79l-91.38-84.66,127.51-138.06,91.91,85.18Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M3251.19,2040.93q46.16,42.39,92.32,84.77"
              transform="translate(-700.15 -108.92)"
            />
          </g>
          <g id="Nuevo_L12" data-name="Nuevo L12" style={{ fill: FALLBACK_COLOR }}>
            <path
              className={classes.cls1}
              d="M3309.09,2347l-92.31-85.19,128-137.54,91.52,84.4Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M3343.51,2125.7l91.26,84.66"
              transform="translate(-700.15 -108.92)"
            />
          </g>
          <g id="Nuevo_L11" data-name="Nuevo L11" style={{ fill: FALLBACK_COLOR }}>
            <path
              className={classes.cls1}
              d="M3400.74,2431.77,3309.09,2347l127.25-138.33,91.78,85.19Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M3434.77,2210.36l92,85"
              transform="translate(-700.15 -108.92)"
            />
          </g>
          <g id="Nuevo_L10" data-name="Nuevo L10" style={{ fill: FALLBACK_COLOR }}>
            <path
              className={classes.cls1}
              d="M3492.25,2516.56l-91.51-84.79,127.38-137.93,91.91,84.65Q3556.15,2447.54,3492.25,2516.56Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M3526.73,2295.34l91.77,84.81"
              transform="translate(-700.15 -108.92)"
            />
          </g>
          <g id="L9_2" style={{ fill: FALLBACK_COLOR }}>
            <path
              className={classes.cls1}
              d="M3492.25,2516.56l89.15,82.15s14.63,11.74,18.19,29.28V2629l58-14,179.86-43.39a3.35,3.35,0,0,0-.18-1.1,3.9,3.9,0,0,0-1.18-1.68C3833.88,2567,3620,2378.5,3620,2378.49h0Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M3618.5,2380.15l212.13,187.18a40.27,40.27,0,0,1,5.39,4.7"
              transform="translate(-700.15 -108.92)"
            />
          </g>
          <g id="L8_2" style={{ fill: FALLBACK_COLOR }}>
            <path
              className={classes.cls1}
              d="M3574.47,2712.65q8.22-20.28,16.42-40.55c4.19-6.85,9.83-18.58,9.69-33.43a58,58,0,0,0-.79-9l-.2-.63,69.8-16.81c84.05-20.26,161.28-38.9,168.09-40.55a2.48,2.48,0,0,1,0,.51,2.77,2.77,0,0,1,0,.33c0,.14,0,.35-.11.6a8.26,8.26,0,0,1-.28.89c-.27.69-61.32,143.41-90.34,211.23Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M3744.72,2784.38l88.81-207s2.61-3.65,2.49-5.32"
              transform="translate(-700.15 -108.92)"
            />
          </g>
          <g id="Nuevo_L7" data-name="Nuevo L7" style={{ fill: FALLBACK_COLOR }}>
            <path
              className={classes.cls1}
              d="M3697.84,2900.56l-172-73.85,48.59-114.06,172.29,72.59Q3722.29,2842.9,3697.84,2900.56Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M3744.72,2784.38l-49.26,115.16"
              transform="translate(-700.15 -108.92)"
            />
          </g>
          <g id="L6_2" style={{ fill: FALLBACK_COLOR }}>
            <path
              className={classes.cls1}
              d="M3525.88,2826.71,3477,2941.84l141.12,146.27q39.87-93.78,79.76-187.55Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M3616.12,3086.59q39.68-93.53,79.34-187.05"
              transform="translate(-700.15 -108.92)"
            />
          </g>
          <g id="L5_2" style={{ fill: FALLBACK_COLOR }}>
            <path
              className={classes.cls1}
              d="M3617.5,3088.64q-53.6,20.74-107.26,41.44-54.09,20.85-108.19,41.6l-3.16-199.52a140.65,140.65,0,0,0,29.14-2c16.22-2.77,35.61-15,43.12-22.68l5.81-5.67Z"
              transform="translate(-700.15 -108.92)"
            />
            <line
              className={classes.cls1}
              x1="2701.91"
              y1="3060.65"
              x2="2915.68"
              y2="2977.96"
            />
          </g>
          <g id="L4_2" style={{ fill: FALLBACK_COLOR }}>
            <path
              className={classes.cls1}
              d="M3398.89,2972.16,3244.6,2937l-41.67,183.82,81.5,21.49,115.38,29.41a4.28,4.28,0,0,0,2.24,0Z"
              transform="translate(-700.15 -108.92)"
            />
            <line
              className={classes.cls1}
              x1="2701.91"
              y1="3060.65"
              x2="2503.26"
              y2="3009.78"
            />
          </g>
          <g id="Nueva_L3" data-name="Nueva L3" style={{ fill: FALLBACK_COLOR }}>
            <path
              className={classes.cls1}
              d="M3202.93,3120.78l-121.71-28.09,41-183.29L3244.6,2937Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M3081.85,3089.86l121.56,28.84"
              transform="translate(-700.15 -108.92)"
            />
          </g>
          <g id="L2_2" style={{ fill: FALLBACK_COLOR }}>
            <path
              className={classes.cls1}
              d="M3122.23,2909.4l-129.1-30.07h-.66l-33.23,185.41s22.29,4.48,32.57,6.85l89.41,21.1Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M3081.85,3089.86l-122.22-27.31"
              transform="translate(-700.15 -108.92)"
            />
          </g>
          <g id="L1_2" style={{ fill: FALLBACK_COLOR }}>
            <path
              className={classes.cls1}
              d="M2959.24,3064.74l-151-20.44L2828.56,2894s13.19-28.62,41.93-30.46l122,15.82Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M2808.58,3041.88s146.6,19.68,151.05,20.67"
              transform="translate(-700.15 -108.92)"
            />
          </g>

          {/* Edificio / áreas comunes - sin tooltip de lote */}
          <g id="edificio" style={{ fill: "#e0e0e0" }}>
            <path
              className={classes.cls1}
              d="M1712.43,1404.25q-15.79,14.82-31.52,29.67a54.39,54.39,0,0,1-29.8,9.83c-19.51.31-33.15-10.71-36.79-13.65-.38-.3-5.53-5.79-15.83-16.75-2.27-2.41-8.45-9-17.14-18.33-4.47-4.79-6.87-7.39-12.13-13-13.39-14.42-20.08-21.63-25.19-27.1-6-6.43-14.41-15.36-24.66-26.11l97.85-91.72q15.29,17,30.86,34,48.33,52.86,96.52,103.12Q1728.49,1389.19,1712.43,1404.25Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M1836.48,1288.84l-91.88,85.35L1617.22,1237l91.74-84.81Q1772.73,1220.53,1836.48,1288.84Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M1903.37,1205.13a49.42,49.42,0,0,1-1.71,13.06c-3.08,11.31-9.53,18.78-13.32,23.07-.91,1-1.26,1.38-12.43,11.69l-13.81,12.75c-6.43,5.89-15.07,13.74-25.62,23.14l-127.68-137q40.52-37.75,81-75.5c29.25,26.76,51.76,47.49,63.54,58.41,22.52,20.87,20,19,23.36,21.76a124.84,124.84,0,0,1,10.55,9.89,65.45,65.45,0,0,1,8.3,10.29,53.54,53.54,0,0,1,4.22,7.91A47.87,47.87,0,0,1,1903.37,1205.13Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M1789.84,1076.3,1709,1152.23l-127.38-137.67c6.41-6,11.51-11.06,14.94-14.48,6.27-6.26,10.92-11.14,18.46-17.67,7.35-6.38,10.09-8,12.66-9.1a48,48,0,0,1,15.29-3.69,55.31,55.31,0,0,1,8.18-.14,54.09,54.09,0,0,1,19.52,2.9,58.55,58.55,0,0,1,16.22,9c3.47,2.57,4.13,3.52,11.86,10.83,5.5,5.19,3.44,3.08,18.65,17.07,2.37,2.18,8,7.41,14.45,13.38,10.77,10,18,16.9,22.16,20.83C1762.12,1051.17,1774,1062.23,1789.84,1076.3Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M1709,1152.23l-91.64,85-128.61-137.1,92.87-85.62Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M1608.37,1245.34,1530,1318.81l-10.62,10q-19.71-21.83-40-43.85-23.05-25-45.95-49.18a64.08,64.08,0,0,1-17.67-49.32,57.56,57.56,0,0,1,10.21-27.31l54.33-50.88,8.44-8L1617.22,1237Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M2039.59,1749.35,2008.21,1779a54.44,54.44,0,0,1-31,9.5c-18.33-.3-31.08-10.48-35.55-14-1.91-1.53-5.16-4.86-11.65-11.54-10.44-10.73-18-19.06-20-21.36-9.39-10.35-7.68-8-39.83-41.93-5.54-5.85-14.09-14.88-24.66-26.11l95-88.49q15,16.17,29.93,32.31,48.75,52.53,97.72,104.84Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M2159.59,1636.6l-91.51,85.59L1940.43,1585l91.65-85Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M2159.59,1636.6,2032.08,1500q24.2-22.18,48.52-44.84,29.63-27.59,58.52-55c19.05,17.39,34.1,31.34,44.51,41,12.89,12,11.81,11.13,15.92,14.84,12.93,11.69,22.79,19.72,36.2,33.32,6.35,6.45,10.4,11,13.45,18.1a50.59,50.59,0,0,1,3.89,17.87c.56,17-7.85,29.77-11.36,34.52l-37.16,35.89Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M2132.35,1406.79,2055,1479.1,2032.08,1500q-27.39-30.52-56.71-61.45-13.21-13.92-26.37-27.43l-44.31-48.79,67.71-63.68a60.62,60.62,0,0,1,61.26,4.34l105.46,97.15Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M2032.08,1500l-91.65,85-128.61-137.1,92.87-85.62Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M1931.78,1593.11c-22.17,20.67-64.11,59.75-86.3,80.42Q1825.4,1653,1805.19,1632q-24.68-25.63-48.7-51.1a97.39,97.39,0,0,1-14.12-29.15,75.43,75.43,0,0,1-.77-16.63,77.1,77.1,0,0,1,7.45-28.27l54.33-50.87,8.44-8L1940.43,1585Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M3032.16,2567.33l136.62,128.57L3075,2795.59l-159.56-36.53a32.59,32.59,0,0,1-3.56-3.29c-9.17-9.86-9.63-22.82-9.81-28.09-.58-16.59,7-29,10.34-33.76Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M3209.79,2826.71,3075,2795.59l179.47-191.07,87,81.83Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M3209.79,2826.71l149.54,37.72a50.79,50.79,0,0,0,26.77-1.58c14.11-4.88,22.15-15.56,24.53-20.58l2.37-5,24.92-60.13-96.46-90.78Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M3437.92,2777.13l38.64-89.14s3-11.21,2.77-19.39c0,0,0-20.44-7.91-32.17s-19.39-21-19.39-21L3340.87,2512.6l-86.38,91.92Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M3340.87,2512.6,3235,2414.76s-10-9.5-22.29-10.29-25.45-.13-34.55,8.05L3118,2476.08l136.48,128.44Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M3254.49,2604.52l-85.71,91.38-136.62-128.57,85.85-91.25Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M2741.26,2457.35s34.42,5.54,60.79,20.18,43,29.8,43,29.8,17.94,17.14,24.14,26.24S2877,2545,2877,2545a29.88,29.88,0,0,0,19.25,7.65c12.13.4,20-7.91,20-7.91l42.6-45.49L2821,2372Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M2958.85,2499.29,2821,2372l85.85-90.72,137.67,127Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M3044.56,2408.3l53.41-56.84s4.35-5.27,6.33-20.57c0,0,1.58-17.14-2.11-28.75s-7.12-15.56-13.72-23.21l-96-88.74-85.58,91.12Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M2992.47,2190.19l-109.71-101.81s-5.41-7-22.16-8-26.76,5.14-28.61,6.59-62.77,67-62.77,67l137.67,127.39Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M2906.89,2281.31,2821,2372l-137.4-127.65,85.58-90.46Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M2683.64,2244.38l-118.55,126.2s-9.1,7-10.42,22,3.43,23.74,3.43,23.74,11.08,17.67,26.64,31c0,0,13.84,15.16,17.93,17.27s15.56,6.07,32,4.88c0,0,25.72-9.36,40.49-11.21,0,0,38.37-6.06,66.06-.92L2821,2372Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M1195.77,980.3l94.15,101s7.12,9.1,22,12.39,35.6-2.11,35.6-2.11a65.63,65.63,0,0,0,11.21-6.59l92.31-86.24L1323,861.88Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M1451.07,998.76l95.07-88.88s9.63-11.74,10-23.34c.13-3.71,1.88-13.79-2.76-24.53a48,48,0,0,0-5.94-9.89,49.16,49.16,0,0,0-9.36-9.23l-98.37-90.33L1323,861.88Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M1439.73,752.56,1278.45,603.15s-11.34-10.15-39-5.8S1200,626.49,1200,626.49L1162.27,689,1323,861.88Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M1162.27,689l-69.75,113.93s-2.9,6.2-3.17,22.69S1094,869.79,1104,881.4s91.78,98.9,91.78,98.9L1323,861.88Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M2061.68,1898.49l369.5,387.89s17.8,15.24,46.68,10.88,54.39-37.38,54.39-37.38l214-228.26a80,80,0,0,0,7.32-43.72c-3-24.72-17-35.21-17-35.21l-346.74-321.23s-10.69-11.47-35.21-14.24-42.53,14.83-45.3,16.81S2127,1804.34,2127,1804.34l-57.76,53s-11.87,9.5-11.67,21.56S2058.88,1895.55,2061.68,1898.49Z"
              transform="translate(-700.15 -108.92)"
            />
            <path
              className={classes.cls1}
              d="M2524.34,1993.44l19.19,7.32,14-33.43,137.87,59.14-30.66,74L2628,2085.62l-20.18,48.06-109.58-47.08,17.21-44.7s3.76-3-1.78-5.14l-6.73-2.57Z"
              transform="translate(-700.15 -108.92)"
            />
          </g>
        </svg>
      </div>
      <MapLegend />
    </>
  );
}
